import React, { useState, useEffect, useCallback } from 'react';
import { Badge, Dropdown, List, Button, Typography, Space, Empty, Spin, message } from 'antd';
import { BellOutlined, CheckOutlined, CloseOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { notificationService } from '../services/api';

const { Text } = Typography;

const NotificationBell = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const navigate = useNavigate();

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const [notifs, stats] = await Promise.all([
        notificationService.getAll({ limit: 10, unread_only: false }),
        notificationService.getStats()
      ]);
      setNotifications(notifs);
      setUnreadCount(stats.unread);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    // Poll every 60 seconds for new notifications
    const interval = setInterval(fetchNotifications, 60000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const handleNotificationClick = async (notification) => {
    try {
      if (!notification.is_read) {
        await notificationService.markAsRead(notification.id);
        setUnreadCount(prev => Math.max(0, prev - 1));
        // Update the notification in the list
        setNotifications(prev =>
          prev.map(n =>
            n.id === notification.id ? { ...n, is_read: true } : n
          )
        );
      }
      if (notification.action_url) {
        navigate(notification.action_url);
      }
      setDropdownOpen(false);
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const handleMarkAllRead = async (e) => {
    e.stopPropagation();
    try {
      await notificationService.markAllAsRead();
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      message.success('All notifications marked as read');
    } catch (error) {
      message.error('Failed to mark notifications as read');
    }
  };

  const handleDismiss = async (e, notification) => {
    e.stopPropagation();
    try {
      await notificationService.dismiss(notification.id);
      setNotifications(prev => prev.filter(n => n.id !== notification.id));
      if (!notification.is_read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      message.error('Failed to dismiss notification');
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical': return '#ff4d4f';
      case 'high': return '#fa8c16';
      case 'medium': return '#1890ff';
      default: return '#8c8c8c';
    }
  };

  const getPriorityIcon = (priority) => {
    if (priority === 'critical' || priority === 'high') {
      return <ExclamationCircleOutlined style={{ color: getPriorityColor(priority), marginRight: 8 }} />;
    }
    return null;
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const dropdownContent = (
    <div style={{
      width: 380,
      maxHeight: 480,
      overflow: 'hidden',
      background: '#fff',
      borderRadius: 8,
      boxShadow: '0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 9px 28px 8px rgba(0, 0, 0, 0.05)'
    }}>
      {/* Header */}
      <div style={{
        padding: '12px 16px',
        borderBottom: '1px solid #f0f0f0',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <Text strong style={{ fontSize: 16 }}>Notifications</Text>
        {unreadCount > 0 && (
          <Button
            type="link"
            size="small"
            icon={<CheckOutlined />}
            onClick={handleMarkAllRead}
          >
            Mark all read
          </Button>
        )}
      </div>

      {/* Notification List */}
      <div style={{ maxHeight: 360, overflow: 'auto' }}>
        <Spin spinning={loading}>
          {notifications.length === 0 ? (
            <Empty
              description="No notifications"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              style={{ padding: 32 }}
            />
          ) : (
            <List
              dataSource={notifications}
              renderItem={(item) => (
                <div
                  onClick={() => handleNotificationClick(item)}
                  style={{
                    padding: '12px 16px',
                    cursor: 'pointer',
                    background: item.is_read ? '#fff' : '#f6ffed',
                    borderLeft: `4px solid ${getPriorityColor(item.priority)}`,
                    borderBottom: '1px solid #f0f0f0',
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = item.is_read ? '#fafafa' : '#e6f7e6'}
                  onMouseLeave={(e) => e.currentTarget.style.background = item.is_read ? '#fff' : '#f6ffed'}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        {getPriorityIcon(item.priority)}
                        <Text
                          strong={!item.is_read}
                          style={{
                            fontSize: 14,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {item.title}
                        </Text>
                      </div>
                      {item.message && (
                        <Text
                          type="secondary"
                          style={{
                            fontSize: 12,
                            display: 'block',
                            marginTop: 4,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {item.message}
                        </Text>
                      )}
                      <Text type="secondary" style={{ fontSize: 11, marginTop: 4, display: 'block' }}>
                        {formatTimeAgo(item.created_at)}
                      </Text>
                    </div>
                    <Button
                      type="text"
                      size="small"
                      icon={<CloseOutlined style={{ fontSize: 12 }} />}
                      onClick={(e) => handleDismiss(e, item)}
                      style={{ marginLeft: 8, opacity: 0.6 }}
                    />
                  </div>
                </div>
              )}
            />
          )}
        </Spin>
      </div>

      {/* Footer */}
      {notifications.length > 0 && (
        <div style={{
          padding: '8px 16px',
          borderTop: '1px solid #f0f0f0',
          textAlign: 'center'
        }}>
          <Button
            type="link"
            onClick={() => {
              navigate('/notifications');
              setDropdownOpen(false);
            }}
          >
            View all notifications
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <Dropdown
      dropdownRender={() => dropdownContent}
      trigger={['click']}
      open={dropdownOpen}
      onOpenChange={setDropdownOpen}
      placement="bottomRight"
    >
      <div style={{ cursor: 'pointer', padding: '0 8px' }}>
        <Badge count={unreadCount} size="small" offset={[-2, 2]}>
          <BellOutlined style={{ fontSize: 20, color: '#fff' }} />
        </Badge>
      </div>
    </Dropdown>
  );
};

export default NotificationBell;
